import {
  IsBoolean,
  IsIn,
  IsObject,
  IsOptional,
  IsString,
  Matches,
  MaxLength,
} from 'class-validator';

/**
 * Notification preference + inbox contracts.
 *
 * These live in `contracts-common` rather than in one portal's contracts
 * package because all three audiences — officers (gov-api), staff (admin-api)
 * and training participants (web-api) — get the identical shape. Each API owns
 * *who* it will answer for; none of them owns the schema.
 *
 * The category and channel string unions are duplicated here from
 * `@munifocus/notifications` on purpose: contracts is a leaf package that
 * browser bundles import, and it must not pull in a package whose Nest subpath
 * reaches BullMQ and TypeORM. `notification-taxonomy.spec.ts` in that lib
 * asserts the two lists stay identical, so the duplication cannot drift.
 */

export const NOTIFICATION_CATEGORY_VALUES = [
  'assignments',
  'deadlines',
  'site-visits',
  'case-progress',
  'escalations',
  'correspondence',
  'training',
  'account',
  'safety',
  'mentions',
  'watching',
  'handovers',
] as const;
export type NotificationCategoryId = (typeof NOTIFICATION_CATEGORY_VALUES)[number];

export const NOTIFICATION_CHANNEL_VALUES = ['email', 'sms', 'push', 'in-app'] as const;
export type NotificationChannelId = (typeof NOTIFICATION_CHANNEL_VALUES)[number];

export const DIGEST_MODE_VALUES = ['immediate', 'daily'] as const;
export type DigestModeId = (typeof DIGEST_MODE_VALUES)[number];

export type ChannelTogglesDto = Record<NotificationChannelId, boolean>;

export interface QuietHoursDto {
  /** `HH:mm`, 24-hour, in {@link NotificationPreferencesDto.timezone}. */
  start: string;
  end: string;
}

export interface NotificationPreferencesDto {
  categories: Record<NotificationCategoryId, ChannelTogglesDto>;
  digest: DigestModeId;
  quietHours: QuietHoursDto | null;
  timezone: string;
  /** Categories the user may not switch off, so the UI can render them locked. */
  mandatoryCategories: NotificationCategoryId[];
}

// --- Requests -------------------------------------------------------------

const HH_MM = /^([01]\d|2[0-3]):([0-5]\d)$/;

/**
 * `HH:mm` shape check for a quiet-hours window.
 *
 * A plain predicate rather than a `@ValidateNested` class: nested validation
 * needs `class-transformer`'s `@Type`, which needs `reflect-metadata`, which
 * this contracts package — imported by browser bundles — has no business
 * pulling in. The window is re-checked server-side by `isValidQuietHours()`
 * before it is stored, so nothing malformed reaches the database either way.
 */
export function isQuietHoursShape(value: unknown): value is QuietHoursDto {
  if (typeof value !== 'object' || value === null) return false;
  const { start, end } = value as Partial<QuietHoursDto>;
  return (
    typeof start === 'string' && typeof end === 'string' && HH_MM.test(start) && HH_MM.test(end)
  );
}

/**
 * A partial update. Every field is optional and categories merge channel by
 * channel, so a client that only renders some of the taxonomy cannot wipe the
 * rest of it — which is what makes adding a category a non-breaking change for
 * an already-deployed portal.
 */
export class UpdateNotificationPreferencesBody {
  @IsOptional()
  @IsObject()
  categories?: Partial<Record<NotificationCategoryId, Partial<ChannelTogglesDto>>>;

  @IsOptional()
  @IsIn(DIGEST_MODE_VALUES)
  digest?: DigestModeId;

  /** `null` clears the window; omit the field to leave it unchanged. */
  @IsOptional()
  @IsObject()
  quietHours?: QuietHoursDto | null;

  @IsOptional()
  @IsString()
  timezone?: string;
}

// --- Push devices ---------------------------------------------------------

/**
 * An Expo push token. Duplicated from `@munifocus/notifications` for the same
 * reason as the unions above: this package must stay importable by the mobile
 * bundle.
 */
export const EXPO_PUSH_TOKEN_SHAPE = /^(ExponentPushToken|ExpoPushToken)\[[A-Za-z0-9_-]+\]$/;

export const PUSH_PLATFORM_VALUES = ['ios', 'android'] as const;
export type PushPlatformId = (typeof PUSH_PLATFORM_VALUES)[number];

/**
 * `POST /notifications/devices` — this phone may now be woken for the signed-in
 * account. The account comes from the token, never from the body.
 */
export class RegisterPushDeviceBody {
  @IsString()
  @MaxLength(255)
  @Matches(EXPO_PUSH_TOKEN_SHAPE)
  token!: string;

  @IsOptional()
  @IsIn(PUSH_PLATFORM_VALUES)
  platform?: PushPlatformId;
}

/** `POST /notifications/devices/unregister` — sign-out, or push switched off. */
export class UnregisterPushDeviceBody {
  @IsString()
  @MaxLength(255)
  token!: string;
}

export class MarkNotificationReadBody {
  @IsBoolean()
  read!: boolean;
}

// --- Inbox ----------------------------------------------------------------

export interface LocalizedTextDto {
  en: string;
  fr: string;
}

export interface NotificationInboxItemDto {
  id: string;
  event: string;
  category: NotificationCategoryId;
  /** Both languages, frozen at write time — see `NotificationEntity`. */
  title: LocalizedTextDto;
  body: LocalizedTextDto;
  linkUrl: string | null;
  read: boolean;
  /** ISO datetime. */
  createdAt: string;
}

export interface NotificationInboxDto {
  items: NotificationInboxItemDto[];
  unread: number;
}

/**
 * The tabs of the officers' unified inbox, each a set of categories. A tab is a
 * view over the same rows as the bell, so read state is shared between them.
 */
export const INBOX_TAB_VALUES = [
  'all',
  'mentions',
  'assignments',
  'watching',
  'deadlines',
  'handovers',
] as const;
export type InboxTabId = (typeof INBOX_TAB_VALUES)[number];

export const INBOX_TAB_CATEGORIES: Record<InboxTabId, readonly NotificationCategoryId[]> = {
  all: [],
  mentions: ['mentions'],
  assignments: ['assignments'],
  watching: ['watching', 'case-progress', 'site-visits', 'escalations'],
  deadlines: ['deadlines'],
  handovers: ['handovers'],
};

/** Unread counts per category, for the tab badges. */
export interface InboxUnreadCountsDto {
  total: number;
  byCategory: Partial<Record<NotificationCategoryId, number>>;
}
